import {useContext} from 'react';

import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import LockIcon from '@mui/icons-material/Lock';
import {Grid, Typography} from '@mui/material';

import {Banner} from '../Banner';

import LightBanner from '../../../assets/locked-banner-light.png';
import {SnackBarContext} from '../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../contexts/UserContext/UserContext';
import {useApiWrite} from '../../../hooks/useApiWrite';
import {Post} from '../../../lib/api/POST/Post';
import {PostRoutes} from '../../../lib/api/POST/PostRoutes';
import {AppConfig} from '../../../lib/AppConfig';
import {ApplicantRoleData, CompanyAdminRoleData} from '../../../types/User';
import {EmpionButton} from '../../EmpionButton/EmpionButton';

interface Props {
  isCompany?: boolean;
  applicantId?: string;
}

export const LockedCultureTypeBanner = ({isCompany, applicantId}: Props) => {
  const {currentUser} = useContext(UserContext);
  const navigate = useNavigate();
  const {t} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);

  const onSuccess = () => {
    dispatchMessage(t('missingCultureModal.successMessage'), 'success');
  };

  const onError = () => {
    dispatchMessage(t('missingCultureModal.errorMessage'), 'error');
  };

  const {makeRequest} = useApiWrite<Post.SendCompleteSurveyToApplicant>({
    method: 'post',
    path: PostRoutes[Post.SendCompleteSurveyToApplicant],
    onSuccess,
    onError,
  });

  const userType = isCompany ? 'company' : 'applicant';

  const onApplicantClick = () => {
    const surveyId = (currentUser?.role.data as ApplicantRoleData).talentSurveyId;

    if (surveyId) {
      navigate(`${AppConfig.TALENT_SURVEY_WITH_ID_PATH}/${surveyId}`);
    }
  };

  const onCompanyClick = () => {
    const companyId = (currentUser?.role.data as CompanyAdminRoleData).companyId;
    // if applicant id missing, we still call BE, so we can be aware of the error.
    makeRequest({talentSurveyRequest: {applicantId: applicantId || '', companyId}});
  };

  return (
    <Banner imageUrl={LightBanner}>
      <Grid container flexDirection='column' justifyContent='center' alignItems='center' padding='40px' gap='20px'>
        <LockIcon sx={{fontSize: 40}} />
        <Typography variant='h2'>{t(`applicantCultureTypeBanner.${userType}.title`)}</Typography>
        <Typography sx={{marginBottom: '32px'}} variant='h3'>
          {t(`applicantCultureTypeBanner.${userType}.subtitle`)}
        </Typography>
        <EmpionButton onClick={isCompany ? onCompanyClick : onApplicantClick}>
          <Typography sx={{paddingX: '32px', color: 'white'}} variant='h4'>
            {t(`applicantCultureTypeBanner.${userType}.button`)}
          </Typography>
        </EmpionButton>
      </Grid>
    </Banner>
  );
};
